import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

//import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { sqlDateToLabel } from 'helpers/helpers';

import useStyles, { RangeStyling } from './styles';
import { RANGE_HEIGHT } from './Chart';
import { EdwardColors } from 'enums';

interface Props {
  presetRange?: number[];
  maxRange: number;
  handleRangeChangeInChart: (newValue: number | number[]) => void;
  svgPosX?: number;
  svgWidth: number;
  //svgMaxY: number;
  //svgCommands: string;
  dates: string[];
  labels: { value: number; label: string }[];
  values: number[];
  domRef: HTMLDivElement | null;
  area?: boolean;
  inititalUpdate?: boolean;
  styling?: RangeStyling;
}

const ChartRange: React.FC<Props> = ({
  maxRange,
  handleRangeChangeInChart,
  svgWidth,
  //svgCommands,
  //svgMaxY,
  svgPosX,
  dates,
  presetRange,
  labels,
  values,
  domRef,
  area,
  inititalUpdate,
  styling,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ overview: false, styling });

  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });
  const smallMobile = useMediaQuery('@media (max-width: 400px)', { noSsr: true });

  //const [svgWidth, setSvgWidth] = useState(0);
  //const [svgPosX, setSvgPosX] = useState(0);
  const [svgCommands, setSvgCommands] = useState('');
  const [range, setRange] = useState<number[]>(presetRange || [0, maxRange]);
  const handleRangeChange = (event: React.ChangeEvent<{}>, newValue: number | number[]) => {
    setRange(newValue as number[]);
  };
  const handleRangeChangeCommitted = (event: React.ChangeEvent<{}>, newValue: number | number[]) => {
    handleRangeChangeInChart(newValue);
  };

  const labelFormatter = (value: number, index: number) => {
    return sqlDateToLabel(dates[value]);
  };

  const responsiveLabels = useMemo(() => {
    if (smallMobile) {
      return [labels[0], labels[labels.length - 1]];
    } else if (tablet || mobile) {
      return labels.filter((l, i) => i % (mobile ? 4 : 2) === 0);
    } else {
      return labels;
    }
  }, [labels, tablet, mobile, smallMobile]);

  useEffect(() => {
    //console.log(presetRange);
    if (presetRange) {
      setRange(presetRange);
      inititalUpdate && handleRangeChangeInChart(presetRange);
    }
  }, [presetRange, handleRangeChangeInChart, inititalUpdate]);

  useEffect(() => {
    //const { current: dom } = domRef;
    //const area = domRef ? domRef.getElementsByClassName('highcharts-plot-border')[0] : null;
    //const chartStartX = area ? Number(area.getAttribute('x')) : 0;
    //const chartAreaWidth = area ? Number(area.getAttribute('width')) : 1;
    //setSvgPosX(chartStartX);
    //setSvgWidth(chartAreaWidth);

    const dataWidth = values.length;
    const height = RANGE_HEIGHT;
    const width = svgWidth;
    const svgMinY = Math.min(...values);
    const nValues = values.map((v) => (v += -svgMinY));
    const svgMaxY = Math.max(...nValues);
    const scaleY = svgMaxY / height;
    if (dataWidth && scaleY) {
      const stepX = width / dataWidth;
      let c = `M0 ${height - nValues[0] / scaleY}`;
      nValues.forEach((v, i) => {
        if (i > 0) {
          c += ` L${i * stepX} ${height - v / scaleY}`;
        }
      });
      c += area
        ? ` L${width} ${height - nValues[nValues.length - 1] / scaleY} L${width} ${height} L${0} ${height} L${0} ${
            height - nValues[0] / scaleY
          } Z`
        : '';
      setSvgCommands(c);
    }
  }, [values, svgWidth, domRef, area]);

  // console.log('redraw cr');

  return (
    <div className={classes.rangeWrapper}>
      <div style={{ color: EdwardColors.GRAY_MEDIUM, paddingLeft: `${svgPosX || 0}px` }}>
        {t('accountPlan_graphRange')}
      </div>
      <div style={{ display: 'flex', paddingLeft: `${svgPosX || 0}px` }}>
        <div>
          <svg
            width={svgWidth}
            height={RANGE_HEIGHT}
            viewBox={`0 0 ${svgWidth} ${RANGE_HEIGHT}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ background: '#f9f9f9' }}
          >
            <path
              d={svgCommands}
              style={{
                fill: area ? 'rgba(186, 130, 53, 0.33)' : 'none',
                stroke: styling?.lineColor || 'rgba(186, 130, 53, 0.5)',
                strokeWidth: 1.5,
              }}
            />
          </svg>
          <Slider
            name={`range`}
            value={range}
            marks={responsiveLabels}
            aria-labelledby="discrete-slider"
            valueLabelDisplay="auto"
            step={1}
            min={0}
            max={maxRange}
            onChange={handleRangeChange}
            onChangeCommitted={handleRangeChangeCommitted}
            className={classes.rangeSlider}
            style={{ width: `${svgWidth}px` }}
            valueLabelFormat={labelFormatter}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(observer(ChartRange));
